<script setup lang="ts">
// Selectable capsule card used for channels (checkbox), roles and modes (radio) in the prototype.
withDefaults(
  defineProps<{
    kind: 'radio' | 'checkbox'
    selected: boolean
    title: string
    description?: string
    disabled?: boolean
  }>(),
  { description: undefined },
)

defineEmits<{ select: [] }>()
</script>

<template>
  <button
    type="button"
    class="ui-choice"
    :class="[`ui-choice--${kind}`, { 'ui-choice--selected': selected }]"
    :role="kind"
    :aria-checked="selected"
    :disabled="disabled"
    @click="$emit('select')"
  >
    <span v-if="kind === 'radio'" class="ui-choice__radio" aria-hidden="true">
      <span v-if="selected" class="ui-choice__dot"></span>
    </span>
    <span class="ui-choice__body">
      <span class="ui-choice__title">
        <slot name="icon" />
        {{ title }}
        <span v-if="kind === 'checkbox'" class="ui-choice__box" aria-hidden="true">
          <svg v-if="selected" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        </span>
      </span>
      <span v-if="description" class="ui-choice__desc">{{ description }}</span>
    </span>
  </button>
</template>

<style scoped>
.ui-choice {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  padding: 14px 16px;
  text-align: left;
  border: 1px solid var(--opaque-separator);
  background: var(--surface-card);
  border-radius: var(--radius-list);
  cursor: pointer;
  color: var(--text-primary);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    background var(--duration-fast) var(--ease-standard);
}
.ui-choice--checkbox {
  min-height: 76px;
  padding: 12px 14px;
}
.ui-choice:hover:not(:disabled) {
  background: var(--surface-hover);
}
.ui-choice--selected,
.ui-choice--selected:hover:not(:disabled) {
  border-color: var(--accent);
  background: var(--surface-card);
}
.ui-choice:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.ui-choice__radio {
  width: 16px;
  height: 16px;
  min-width: 16px;
  margin-top: 1px;
  border-radius: 50%;
  border: 1px solid var(--muted-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ui-choice--selected .ui-choice__radio {
  border-color: var(--accent);
}
.ui-choice__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
}
.ui-choice__body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
  min-width: 0;
}
.ui-choice__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--type-body-size);
  font-weight: var(--weight-semibold);
}
.ui-choice__box {
  margin-left: auto;
  width: 16px;
  height: 16px;
  border-radius: var(--radius-xs);
  border: 1px solid var(--muted-2);
  color: var(--on-accent);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ui-choice--selected .ui-choice__box {
  border-color: var(--accent);
  background: var(--accent);
}
.ui-choice__desc {
  font-size: var(--type-footnote-size);
  line-height: var(--type-footnote-line);
  color: var(--text-secondary);
}
</style>
